import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import {
  FunFormLayout,
  useFunFormLayout,
  type FunFormLayoutParent,
} from '@/funform'

export const Route = createFileRoute('/test/funform')({
  component: FunFormTestPage,
})

const EXAMPLE_LAYOUT: FunFormLayoutParent = {
  id: 'example-form',
  type: 'parent',
  title: 'Example Form',
  children: [
    {
      id: 'name',
      type: 'text',
      title: 'Name',
      helpText: 'Enter your full name',
    },
    {
      id: 'email',
      type: 'text',
      title: 'Email',
      helpText: 'Enter your email address',
    },
  ],
}

function FunFormTestPage() {
  const [layoutJson, setLayoutJson] = useState(JSON.stringify(EXAMPLE_LAYOUT, null, 2))
  const [layoutError, setLayoutError] = useState<string | null>(null)

  const parsedLayout = useMemo(() => {
    try {
      setLayoutError(null)
      const parsed = JSON.parse(layoutJson)
      return parsed as FunFormLayoutParent
    } catch (e) {
      setLayoutError(e instanceof Error ? e.message : 'Invalid JSON')
      return null
    }
  }, [layoutJson])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          FunForm Test Page
        </h1>

        <div className="grid grid-cols-2 gap-8">
          {/* Left side: Layout JSON input */}
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Layout Configuration (JSON)
              </label>
              <textarea
                value={layoutJson}
                onChange={(e) => setLayoutJson(e.target.value)}
                className="w-full h-96 p-4 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                spellCheck="false"
              />
              {layoutError && (
                <p className="text-red-600 dark:text-red-400 text-sm mt-2">
                  Error: {layoutError}
                </p>
              )}
            </div>
          </div>

          {/* Right side: Form and State */}
          <div className="flex flex-col gap-8">
            {/* Form rendering */}
            {parsedLayout ? (
              <FormDisplay layout={parsedLayout} />
            ) : (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-4">
                <p className="text-yellow-800 dark:text-yellow-300">
                  Invalid JSON. Please fix the layout configuration.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function FormDisplay({ layout }: { layout: FunFormLayoutParent }) {
  const { state, Provider } = useFunFormLayout()
  const stateJson = JSON.stringify(
    state.data,
    null,
    2
  )

  return (
    <Provider>
      <div className="flex flex-col gap-8">
        {/* Form section */}
        <div>
          <label className="block text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Form Preview
          </label>
          <div className="bg-white dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600 p-6">
            <FunFormLayout layout={layout} />
          </div>
        </div>

        {/* State section */}
        <div>
          <label className="block text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Current State (JSON)
          </label>
          <textarea
            value={stateJson}
            readOnly
            className="w-full h-48 p-4 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none"
            spellCheck="false"
          />
        </div>
      </div>
    </Provider>
  )
}
